import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { configs } from '@constants/configs/configs';

interface INoApplicationsFoundProps {
    year: number | string;
}

const IMAGE_FILE_NAME = 'magnifier_no_results.svg';
const FALLBACK_IMAGE = '/assets/images/magnifier_no_results.svg';

/**
 * Validates and sanitizes image source URL to prevent XSS attacks.
 * Only allows same-origin URLs or relative paths.
 */
const validateImageSrc = (src: string): string => {
    // Check for safe relative paths first (starts with / but not //)
    if (src.startsWith('/') && !src.startsWith('//')) {
        return src;
    }

    try {
        const url = new URL(src, window.location.origin);
        // Only allow same-origin URLs
        if (url.origin === window.location.origin) {
            return src;
        }
        return FALLBACK_IMAGE;
    } catch {
        return FALLBACK_IMAGE;
    }
};

const NoApplicationsFound: React.FC<INoApplicationsFoundProps> = ({ year }) => {
    const imageSrc = configs.ENV.DEVELOPMENT 
        ? `/assets/images/${IMAGE_FILE_NAME}`
        : configs.endpoints.IMAGES.processReplacements({ 
            relativePath: '/resources/img', 
            fileName: IMAGE_FILE_NAME 
        });

    const safeImageSrc = useMemo(() => validateImageSrc(imageSrc), [imageSrc]);

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        e.currentTarget.src = FALLBACK_IMAGE;
    };

    return (
        <div className="no-applications-found">
            <div className="no-applications-found__illustration">
                <img 
                    src={safeImageSrc} 
                    alt="" 
                    aria-hidden="true"
                    onError={handleImageError}
                />
            </div>
            <h3 className="no-applications-found__title">
                <FormattedMessage 
                    id="historicalApp.no_applications_title" 
                    values={{ year }} 
                />
            </h3>
            <p className="no-applications-found__description">
                <FormattedMessage id="historicalApp.no_applications_description" />
            </p>
            <ul className="no-applications-found__hints">
                <li>
                    <FormattedMessage id="historicalApp.no_applications_hint_year" />
                </li>
                <li>
                    <FormattedMessage 
                        id="historicalApp.no_applications_hint_verify" 
                        values={{ year }} 
                    />
                </li>
            </ul>
        </div>
    );
};

export default NoApplicationsFound;
